/**
 * tm/pages/MobileCallList.tsx — 휴대폰 업체 화면(/m)의 「통화 녹음」 묶음 (2026-10-28 지도사 「고객과 통화한 것도 · 업로드 후 담당 팀장님에게 문자로 안내」)
 *  최근 통화 먼저 · 줄 = 통화 시각 · 길이 · 정리 상태 · 문자 상태 → 누르면 아래에서 판: 녹음 · 대화 글(발언 누르면 재생 · 화자 이름) · 팀장님께 문자(다시)
 *  정리 중인 녹음이 있으면 30초마다 다시 불러옴
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { G, Icon, clamp } from '../portal-ui/tds';
import RecordingView, { clock } from '../components/RecordingView';
import { Button, Card, CardHead, ROW, Sheet, TOSS } from './mobileKit';
import type { CallRec, CallTarget } from '../callRec';
import { callWhen, listCalls, noticeText, notifyCall, notifyLine, notifyToast, recCall, showPhone } from '../callRec';

const STATE: Record<string, string> = { queued: '정리 기다림', processing: '정리 중', failed: '정리 못 함' };

export default function MobileCallList({ companyId, companyName, goCall, toast, link }: {
  companyId: string; companyName: string; goCall: () => void; toast: (m: string) => void; link: (children: ReactNode, onClick: () => void) => ReactNode;
}) {
  const [data, setData] = useState<{ list: CallRec[]; targets: CallTarget[]; ready: boolean } | null>(null);
  const [err, setErr] = useState(false);
  const [openId, setOpenId] = useState('');
  const [lim, setLim] = useState(3);
  const load = useCallback(async () => {
    try { setData(await listCalls(companyId)); setErr(false); } catch { setErr(true); setData(d => d || { list: [], targets: [], ready: true }); }
  }, [companyId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!data || !data.list.some(x => x.status === 'processing' || (x.status === 'queued' && data.ready) || !!x.relabeling)) return undefined;   // 화자 다시 나누는 중도(2026-10-29)
    const t = window.setTimeout(() => { void load(); }, 30000);
    return () => window.clearTimeout(t);
  }, [data, load]);
  const list = data ? data.list : [];
  const open = list.find(x => x.id === openId) || null;
  const act = async (fn: () => Promise<unknown>, okMsg: string | ((r: unknown) => string)) => { try { const r = await fn(); toast(typeof okMsg === 'function' ? okMsg(r) : okMsg); await load(); } catch (e: unknown) { toast(e instanceof Error ? e.message : '저장하지 못했어요'); } };

  return (
    <Card pad="4px 0 6px">
      <CardHead title="통화 녹음" count={data ? list.length : undefined} right={link('＋ 녹음 올리기', goCall)} />
      <div style={{ padding: '0 20px' }}>
        {data === null ? <div style={ROW.empty}>불러오는 중이에요</div>
          : err && !list.length ? <div style={ROW.empty}>통화 녹음을 못 불러왔어요</div>
            : !list.length ? <div style={ROW.empty}>올린 통화 녹음이 없어요</div>
              : list.slice(0, lim).map(r => {
                const st = r.status === 'done' ? '' : STATE[r.status] || '';
                // 다른 줄과 같은 틀 — 제목 = 통화 주제(무엇) · 둘째 줄 = 언제 · 길이 · 번호(2026-10-28)
                const sub = [callWhen(r.call_at || r.created_at), r.duration_ms ? clock(r.duration_ms) : '', r.phone ? showPhone(r.phone) : ''].filter(Boolean).join(' · ');
                return (
                  <button key={r.id} type="button" className="tds-row" onClick={() => setOpenId(r.id)} aria-label={`${callWhen(r.call_at || r.created_at)} 통화 열기`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: ROW.minHeight, padding: '8px 0', border: 'none', borderBottom: `1px solid ${G.g100}`, background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ ...ROW.title, ...clamp(1) }}>{r.memo || '고객 통화'}</span>
                      {sub && <span style={{ ...ROW.sub, ...clamp(1) }}>{sub}</span>}
                    </span>
                    {st && <span style={{ ...ROW.state, color: r.status === 'failed' ? TOSS.red : TOSS.blue }}>{st}</span>}
                    <Icon name="r" size={18} color={G.g400} sw={2} />
                  </button>
                );
              })}
        {list.length > lim && link(`${list.length - lim}개 더 보기`, () => setLim(n => n + 10))}
      </div>
      {open && data && (
        <Sheet open title={`${callWhen(open.call_at || open.created_at) || '통화'} 통화`} onClose={() => setOpenId('')}>
          <CallDetail rec={open} targets={data.targets} keyReady={data.ready} companyName={companyName}
            onMemo={m => void act(() => recCall('PUT', `/${open.id}/memo`, { memo: m }), '통화 주제를 저장했어요')}
            onRename={sp => void act(() => recCall('PUT', `/${open.id}/speakers`, { speakers: sp }), '화자 이름을 저장했어요')}
            onMerge={(from, to) => void act(() => recCall('PUT', `/${open.id}/merge-speaker`, { from, to }), '화자를 합쳤어요')}
            onRetry={() => void act(() => recCall('POST', `/${open.id}/retry`), '다시 정리하고 있어요')}
            onSpeakerCount={n => void act(() => recCall('PUT', `/${open.id}/speaker-count`, { n }), `화자를 ${n}명 안에서 다시 나누고 있어요 · 몇 분 걸려요`)}
            onDelete={() => { setOpenId(''); void act(() => recCall('DELETE', `/${open.id}`), '녹음을 지웠어요'); }}
            onNotify={ids => act(() => notifyCall(open.id, ids, open.status !== 'done'), r => notifyToast((r as { state: string }).state))} />   {/* 실제 결과대로(실패도 「보냈어요」로 뜨던 것 · 2026-10-28) */}
        </Sheet>
      )}
    </Card>
  );
}

/** 판 안 — 메모 · 문자 상태 · 녹음/대화 글 · 팀장님께 문자 */
function CallDetail({ rec, targets, keyReady, companyName, onMemo, onRename, onMerge, onRetry, onSpeakerCount, onDelete, onNotify }: {
  rec: CallRec; targets: CallTarget[]; keyReady: boolean; companyName: string;
  onMemo: (m: string) => void; onRename: (sp: Record<string, string>) => void; onMerge: (from: string, to: string) => void; onRetry: () => void; onSpeakerCount: (n: number) => void; onDelete: () => void; onNotify: (ids: string[]) => Promise<void>;
}) {
  const sendable = targets.filter(t => !t.why);
  const [writing, setWriting] = useState(false);
  const [ids, setIds] = useState<string[]>(() => sendable.map(t => t.id));
  const [busy, setBusy] = useState(false);
  const line = notifyLine(rec.notify, targets, keyReady);
  return (
    <div>
      {(rec.memo || line) && (
        <div style={{ margin: '0 0 12px', fontSize: 15, color: TOSS.sub, lineHeight: 1.6, wordBreak: 'keep-all' }}>
          {rec.memo && <div style={{ color: TOSS.text, whiteSpace: 'pre-wrap' }}>{rec.memo}</div>}
          {line && <div>{line}</div>}
        </div>
      )}
      <RecordingView rec={rec} size="portal" defaultOpen editable keyReady={keyReady} accent={TOSS.blue} title={rec.phone ? showPhone(rec.phone) : '통화 녹음'} memo={rec.memo} onMemo={onMemo} onRename={onRename} onMerge={onMerge} onRetry={onRetry} onSpeakerCount={onSpeakerCount} onDelete={onDelete} />
      {sendable.length > 0 && (writing ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub, marginBottom: 8 }}>팀장님께 문자</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {sendable.map(t => {
              const on = ids.includes(t.id);
              return <button key={t.id} type="button" className="tds-btn" aria-pressed={on} onClick={() => setIds(v => (on ? v.filter(x => x !== t.id) : [...v, t.id]))}
                style={{ minHeight: 48, padding: '0 16px', borderRadius: 22, border: `${on ? 2 : 1}px solid ${on ? TOSS.blue : G.g300}`, background: on ? TOSS.blueLt : '#fff', color: on ? TOSS.blueDk : TOSS.sub, fontSize: 16, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' }}>{on ? '✓ ' : ''}{t.name} {t.title || '팀장'}님</button>;
            })}
          </div>
          {ids.length > 0 && <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 14, background: TOSS.bg, fontSize: 15, color: TOSS.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{noticeText(companyName, sendable.find(t => t.id === ids[0]) || { name: '' })}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8, marginTop: 10 }}>
            <Button kind="gray" onClick={() => setWriting(false)}>닫기</Button>
            <Button muted={!ids.length || busy} onClick={() => { if (!ids.length || busy) return; setBusy(true); void onNotify(ids).finally(() => { setBusy(false); setWriting(false); }); }}>{busy ? '보내는 중' : rec.status === 'done' ? `${ids.length}명에게 보내기` : `정리되면 ${ids.length}명에게`}</Button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}><Button kind="weak" h={52} onClick={() => setWriting(true)}>{rec.notify && ['sent', 'partial'].includes(rec.notify.state) ? '팀장님께 문자 다시 보내기' : '팀장님께 문자 보내기'}</Button></div>
      ))}
    </div>
  );
}
